<script setup lang="ts">
import { ref } from 'vue';

import { MapComponent } from '@apps/main/new-desktop/modules/maps';

import { AppIconButton, AppLazyLoadComponent } from '@libs/ui';

import type { MapConfig } from './Maps.model';

interface StaticMapProps {
  mapConfiguration: MapConfig;
  shouldLazyLoadMap?: boolean;
}

const props = withDefaults(defineProps<StaticMapProps>(), {
  shouldLazyLoadMap: true,
});

interface StaticMapEmit {
  (event: 'zoom'): void;
}

const emit = defineEmits<StaticMapEmit>();

const isMapLoading = ref<boolean>(true);

function mapCreated(): void {
  isMapLoading.value = false;
}
</script>

<template>
  <div class="static-map">
    <div class="title">{{ $t('planner.location') }}</div>
    <AppLazyLoadComponent v-if="shouldLazyLoadMap">
      <div class="map-wrapper">
        <MapComponent
          :class="{
            'map-loading': isMapLoading,
          }"
          gesture-handling="cooperative"
          :show-all-markers="false"
          :markers="
            props.mapConfiguration.markers
              ? [props.mapConfiguration.markers]
              : []
          "
          :marker-formatters="props.mapConfiguration.markerFormatters"
          :start-rectangle="props.mapConfiguration.startRectangle"
          :toggle-to-static-map="true"
          :map-type="props.mapConfiguration.mapType"
          @map-created="mapCreated"
        />
        <AppIconButton
          class="zoom-button"
          type="secondary"
          state="enabled"
          size="medium"
          :icon="{ name: 'zoom' }"
          :ariaLabel="$t('a11y.maximise_map')"
          @click="emit('zoom')"
        />
      </div>
    </AppLazyLoadComponent>
    <div v-else class="map-wrapper">
      <MapComponent
        :class="{
          'map-loading': isMapLoading,
        }"
        gesture-handling="cooperative"
        :show-all-markers="false"
        :markers="
          props.mapConfiguration.markers ? [props.mapConfiguration.markers] : []
        "
        :marker-formatters="props.mapConfiguration.markerFormatters"
        :start-rectangle="props.mapConfiguration.startRectangle"
        :toggle-to-static-map="true"
        :map-type="props.mapConfiguration.mapType"
        @map-created="mapCreated"
      />
      <AppIconButton
        class="zoom-button"
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'zoom' }"
        :ariaLabel="$t('a11y.maximise_map')"
        @click="emit('zoom')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.static-map {
  height: fit-content;
  .title {
    margin-bottom: var(--spacing-16);
    color: var(--color-neutral-70);
    @include font-heading-5;
  }

  .map-wrapper {
    // NOTE: vue3-maps sets max-width and max-height inside, which trimms static map,
    // to size smaller than column width. Using :deep() does not help,
    // request is sent for smaller map.
    width: 100%;
    aspect-ratio: 5/3;
    border-radius: var(--border-radius-12);
    overflow: hidden;
    position: relative;
    margin-bottom: var(--spacing-16);

    @include for-less-or-equal-than-1200-px {
      aspect-ratio: 7/5;
    }

    .zoom-button {
      position: absolute;
      top: 0;
      right: 0;
      margin: var(--spacing-16);
      box-shadow: var(--shadow);
      cursor: pointer;
      @include for-less-or-equal-than-1200-px {
        margin: var(--spacing-12);
      }
    }

    .map-loading {
      position: relative;
      &::after {
        @include animated-background-secondary;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
